import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    signal,
    WritableSignal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import {
    FieldIntervention,
    FieldInterventionControllerGet200Response,
    FieldInterventionsService,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, TranslocoPipe],
    selector: 'app-user-detail-field-intervention-list',
    templateUrl: './user-detail-field-intervention-list.component.html',
})
export class UserDetailFieldInterventionListComponent {
    public readonly fieldInterventions: WritableSignal<FieldIntervention[] | null> = signal<FieldIntervention[] | null>(
        null,
    );
    public minDisplay = 5;
    public readonly personId: InputSignal<string> = input.required<string>();
    public readonly previousUserId: WritableSignal<string | undefined> = signal(undefined);
    public readonly showAll: WritableSignal<boolean> = signal(false);
    public readonly totalFieldInterventionsCount: WritableSignal<number> = signal(0);

    protected readonly ButtonType = ButtonType;
    protected readonly IconPosition = IconPosition;

    private fieldInterventionsService = inject(FieldInterventionsService);

    public constructor() {
        effect(async () => {
            if (this.previousUserId() === this.personId()) {
                return;
            }
            this.totalFieldInterventionsCount.set(0);
            await this.fetchFieldInterventions();
            this.previousUserId.set(this.personId());
        });
    }

    public async toggleShow(): Promise<void> {
        this.showAll.set(!this.showAll());
        await this.fetchFieldInterventions();
    }

    private async fetchFieldInterventions(): Promise<void> {
        if (
            this.totalFieldInterventionsCount() !== 0 &&
            this.fieldInterventions()?.length === this.totalFieldInterventionsCount()
        ) {
            return;
        }

        let totalItems = 0;

        let hasNextPage = false;
        const paginationParameters: PaginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
            take: this.minDisplay,
        };

        const fieldInterventions: FieldIntervention[] = [];

        do {
            const paginatedFieldInterventions: FieldInterventionControllerGet200Response = await firstValueFrom(
                this.fieldInterventionsService.fieldInterventionControllerGet({
                    ...paginationParameters,
                    person: this.personId(),
                    status: FieldIntervention.StatusEnum.ONGOING,
                }),
            );

            fieldInterventions.push(...paginatedFieldInterventions.items);

            if (totalItems === 0) {
                totalItems = paginatedFieldInterventions.totalItems;
            }

            hasNextPage = paginatedFieldInterventions.hasNextPage;
            paginationParameters.page += 1;
        } while (hasNextPage && this.showAll());

        this.fieldInterventions.set(fieldInterventions);

        this.totalFieldInterventionsCount.set(totalItems);
    }
}
